import { useMemo } from 'react';

import type { Place } from '../types/warehouse.types';
import {
  calculatePlaceStats,
  usePlaceStats,
  type PlaceStats,
} from './usePlaceStats';

export interface TerminalOverviewGroup {
  label: string;
  abbr: string;
  total: number;
  occupied: number;
  empty: number;
  blocked: number;
  occupiedRate: number;
  blockedRate: number;
}

export interface TerminalOverviewStats {
  all: PlaceStats;
  groups: TerminalOverviewGroup[];
}

export function useTerminalOverviewStats(allPlaces: Place[]): TerminalOverviewStats {
  // Virtual places (missing/additional) are not physical storage — keep them out of stats
  const physicalPlaces = useMemo(
    () => allPlaces.filter((place) => !place.isVirtual),
    [allPlaces]
  );
  const all = usePlaceStats(physicalPlaces);

  const groups = useMemo<TerminalOverviewGroup[]>(
    () => {
      const groupedPlaces = [
        {
          label: 'Cold Storage',
          abbr: 'CS',
          places: physicalPlaces.filter(
            (place) => !place.is_preparation && !place.isCrossDock && !place.isExternal
          ),
        },
        {
          label: 'CrossDock',
          abbr: 'CD',
          places: physicalPlaces.filter((place) => place.isCrossDock === true),
        },
        {
          label: 'Preparation',
          abbr: 'Prep',
          places: physicalPlaces.filter((place) => place.is_preparation === true),
        },
      ];

      return groupedPlaces.map(({ label, abbr, places }) => ({
        label,
        abbr,
        ...calculatePlaceStats(places),
      }));
    },
    [physicalPlaces]
  );

  return { all, groups };
}
